import React from 'react';
import Modal from 'react-modal';
import { FaTimes, FaDownload, FaStar, FaRegStar } from 'react-icons/fa';
import { GeneratedImage, Tag } from '../types';

// 设置Modal的根元素
Modal.setAppElement('#root');

interface ImageDetailsProps {
  image: GeneratedImage | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (image: GeneratedImage) => void;
  onToggleFavorite: (imageId: string) => void;
  tags: Tag[];
  getModelDisplayName: (modelId: string) => string;
}

const ImageDetails: React.FC<ImageDetailsProps> = ({
  image,
  isOpen,
  onClose,
  onDownload,
  onToggleFavorite,
  tags,
  getModelDisplayName
}) => {
  if (!image) return null;

  // 获取风格样式名称
  const getStyleDisplayName = () => {
    if (!image.style) return '无';
    return image.style.name;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="图像详情"
      className="theme-modal-bg rounded-lg max-w-4xl mx-auto outline-none overflow-hidden shadow-xl"
      overlayClassName="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
    >
      <div className="flex flex-col md:flex-row">
        {/* 图像预览 */}
        <div className="md:w-1/2 bg-dark-900 flex items-center justify-center p-4">
          <img 
            src={image.url} 
            alt={image.prompt.substring(0, 30)} 
            className="max-w-full max-h-[60vh] object-contain rounded-md shadow-lg" 
          />
        </div>
        
        {/* 图像信息 */}
        <div className="md:w-1/2 p-6 overflow-y-auto max-h-[80vh] image-details-scrollbar">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold theme-text-primary">图像详情</h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-dark-700 theme-text-secondary hover:text-white transition-colors"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="space-y-4">
            {/* 基本信息 */}
            <div>
              <h3 className="text-md font-medium theme-text-secondary mb-2">基本信息</h3>
              <div className="theme-dropdown-bg rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="theme-text-secondary">创建时间</span>
                  <span className="theme-text-primary">{new Date(image.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="theme-text-secondary">宽高比</span>
                  <span className="theme-text-primary">{image.aspect_ratio}</span>
                </div>
                {image.resolution && (
                  <div className="flex justify-between">
                    <span className="theme-text-secondary">分辨率</span>
                    <span className="theme-text-primary">{image.resolution}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="theme-text-secondary">模型</span>
                  <span className="theme-text-primary">{getModelDisplayName(image.model)}</span>
                </div>
                {image.style && (
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                      <span className="theme-text-secondary">风格样式</span>
                      <span className="theme-text-primary">{getStyleDisplayName()}</span>
                    </div>
                    {image.style.description && (
                      <div className="text-xs theme-text-secondary text-right mt-1">{image.style.description}</div>
                    )}
                  </div>
                )}
                {image.seed && (
                  <div className="flex justify-between">
                    <span className="theme-text-secondary">种子值</span>
                    <span className="theme-text-primary">{image.seed}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* 提示词 */}
            <div>
              <h3 className="text-md font-medium theme-text-secondary mb-2">提示词</h3>
              <div className="theme-dropdown-bg rounded-lg p-3">
                <p className="theme-text-primary whitespace-pre-wrap">{image.prompt}</p>
              </div>
            </div>
            
            {/* 负面提示词 */}
            {image.negative_prompt && (
              <div>
                <h3 className="text-md font-medium theme-text-secondary mb-2">负面提示词</h3>
                <div className="theme-dropdown-bg rounded-lg p-3">
                  <p className="theme-text-primary whitespace-pre-wrap">{image.negative_prompt}</p>
                </div>
              </div>
            )}
            
            {/* 标签 */}
            {image.tags && image.tags.length > 0 && (
              <div>
                <h3 className="text-md font-medium theme-text-secondary mb-2">标签</h3>
                <div className="flex flex-wrap gap-2">
                  {image.tags.map(tagId => {
                    const tag = tags.find(t => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span 
                        key={tagId}
                        className="px-2 py-1 rounded-full text-sm theme-tag-bg theme-tag-text"
                        style={{ borderLeft: `3px solid ${tag.color}` }}
                      >
                        {tag.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* 操作按钮 */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => onDownload(image)}
                className="btn-primary flex items-center justify-center w-10 h-10 rounded-full"
                title="下载图片"
              >
                <FaDownload size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImageDetails;
